import React, { useState, useContext, useMemo } from 'react'
import {
	Alert,
	Button,
	Col,
	Modal,
	ModalBody,
	Row,
	Table
} from 'reactstrap'
import { SpinnerComponent } from 'react-element-spinner'

import { SessionContext } from '../../contexts/SessionProvider'
import AuctionTabsService from '../../services/AuctionTabsService'
import { trigger } from '../../events/customEvents'

/**
 * BulkReleaseToAuctionModal
 * 
 * BRD: "Ability to release units into live auction (individually or bulk release)"
 * BRD: "System should not allow release of a unit if prices are not placed in all three sections"
 * 
 * This modal confirms bulk releasing multiple vehicles from "Awaiting Seller Release" status to "Live" auction.
 * Shows validation warnings for any vehicles missing required pricing.
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Array} auctions - Array of auction objects to release
 * @param {Function} onClose - Callback when modal is closed without action
 * @param {Function} onSuccess - Callback when release is successful
 */
const BulkReleaseToAuctionModal = ({ isOpen, auctions = [], onClose, onSuccess }) =>
{
	const sessionContext = useContext(SessionContext)
	const [isBusy, setIsBusy] = useState(false)
	const [results, setResults] = useState(null)

	/**
	 * Validate pricing for each auction
	 */
	const validationResults = useMemo(() => {
		return auctions.map(auction => {
			const missingPrices = []
			const vin = auction?.vin || auction?.serialNumber || auction?.auctionVehicle?.vehicle?.serialNumber || 'Unknown'
			
			// Check MBP Price
			const mbpPrice = auction.mbpPrice ?? auction.MBPPrice ?? auction.marketPrice
			if (mbpPrice == null || mbpPrice <= 0) {
				missingPrices.push('MBP')
			}
			
			// Check Start Price
			const startPrice = auction.startPrice ?? auction.StartPrice
			if (startPrice == null || startPrice <= 0) {
				missingPrices.push('Start')
			}
			
			// Check Reserve Price
			const reservePrice = auction.reservePrice ?? auction.ReservePrice
			if (reservePrice == null || reservePrice <= 0) {
				missingPrices.push('Reserve')
			}

			return {
				auctionId: auction.auctionId,
				vin,
				isValid: missingPrices.length === 0,
				missingPrices
			}
		})
	}, [auctions])

	const validAuctions = validationResults.filter(v => v.isValid)
	const invalidAuctions = validationResults.filter(v => !v.isValid)
	const canRelease = validAuctions.length > 0

	const handleBulkRelease = () =>
	{
		if (!canRelease) return

		setIsBusy(true)
		setResults(null)

		const auctionIds = validAuctions.map(v => v.auctionId)

		AuctionTabsService
			.bulkReleaseToAuction(auctionIds, sessionContext.loginSession.user.userSession.sessionKey)
			.then((response) =>
			{
				setIsBusy(false)
				const data = response.data?.actionResult || response.data

				if (data?.success !== false)
				{
					const successCount = data?.successCount || auctionIds.length
					const failedCount = data?.failedCount || 0

					if (failedCount > 0) {
						setResults({
							success: true,
							message: `Queued ${successCount} vehicle(s) for release. ${failedCount} failed.`,
							details: data?.results || []
						})
					} else {
						trigger('raiseNotification', { 
							title: 'Bulk Release Queued', 
							message: `Successfully queued ${successCount} vehicle(s) for release. Auctions will go live shortly.`, 
							type: 'success', 
							duration: 5 
						})
						onSuccess && onSuccess()
					}
				}
				else
				{
					trigger('raiseNotification', { 
						title: 'Bulk Release Failed', 
						message: data?.message || 'Failed to release vehicles to auction.', 
						type: 'danger', 
						duration: 8 
					})
				}
			})
			.catch((error) =>
			{
				setIsBusy(false)
				console.error('Bulk release to auction error:', error)
				trigger('raiseNotification', { 
					title: 'Error', 
					message: error?.response?.data?.message || 'An error occurred while releasing vehicles.', 
					type: 'danger', 
					duration: 8 
				})
			})
	}

	const handleClose = () =>
	{
		setResults(null)
		onClose && onClose()
	}

	return (
		<Modal isOpen={isOpen} toggle={handleClose} size="lg" centered>
			<SpinnerComponent loading={isBusy} position="centered" />
			<ModalBody>
				<div className="modal-banner">
					<h3 className="modal-banner-title">Bulk Release to Auction</h3>
				</div>

				{results ? (
					<>
						<Alert color={results.success ? 'info' : 'danger'}>
							{results.message}
						</Alert>
						{results.details && results.details.length > 0 && (
							<Table size="sm" bordered>
								<thead>
									<tr>
										<th>VIN</th>
										<th>Status</th>
										<th>Message</th>
									</tr>
								</thead>
								<tbody>
									{results.details.map((r, i) => (
										<tr key={i}>
											<td>{r.vin}</td>
											<td>{r.success ? '✓' : '✗'}</td>
											<td>{r.message}</td>
										</tr>
									))}
								</tbody>
							</Table>
						)}
						<Row className="mt-3">
							<Col className="text-center">
								<Button color="primary" onClick={() => { setResults(null); onSuccess && onSuccess() }}>
									Close
								</Button>
							</Col>
						</Row>
					</>
				) : (
					<>
						<p className="mt-3">
							You have selected <strong>{auctions.length}</strong> vehicle(s) for release to auction.
						</p>

						{invalidAuctions.length > 0 && (
							<Alert color="warning">
								<strong>{invalidAuctions.length} vehicle(s)</strong> cannot be released due to missing required pricing:
								<Table size="sm" className="mt-2 mb-0" style={{ backgroundColor: 'transparent' }}>
									<thead>
										<tr>
											<th>VIN</th>
											<th>Missing Prices</th>
										</tr>
									</thead>
									<tbody>
										{invalidAuctions.map((v, i) => (
											<tr key={i}>
												<td><code>{v.vin}</code></td>
												<td>{v.missingPrices.join(', ')}</td>
											</tr>
										))}
									</tbody>
								</Table>
							</Alert>
						)}

						{validAuctions.length > 0 && (
							<Alert color="success">
								<strong>{validAuctions.length} vehicle(s)</strong> are ready to be released:
								<ul className="mb-0 mt-2">
									{validAuctions.slice(0, 10).map((v, i) => (
										<li key={i}><code>{v.vin}</code></li>
									))}
									{validAuctions.length > 10 && (
										<li>...and {validAuctions.length - 10} more</li>
									)}
								</ul>
							</Alert>
						)}

						{!canRelease && (
							<Alert color="danger">
								No vehicles can be released. Please ensure all selected vehicles have MBP, Start, and Reserve prices set.
							</Alert>
						)}

						<Row className="mt-4">
							<Col md="6" className="text-end">
								<Button 
									color="success" 
									onClick={handleBulkRelease}
									disabled={!canRelease || isBusy}
								>
									Release {validAuctions.length} Vehicle(s)
								</Button>
							</Col>
							<Col md="6">
								<Button color="secondary" outline onClick={handleClose}>
									Cancel
								</Button>
							</Col>
						</Row>
					</>
				)}
			</ModalBody>
		</Modal>
	)
}

export default BulkReleaseToAuctionModal
